import { useTranslate } from 'react-admin';
import {
  Alert,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type {
  HistoryList,
  HistoryQueue,
  HistoryRow,
  HistoryStatus,
} from 'src/pages/admin/services/vaultOperationsService';

import { HISTORY_PAGE_SIZE, OPERATIONS_NS as NS, OUTCOME_BUTTON_SX } from './constants';
import { adminName, doneBySystem, money2, utcZoneLabel } from './helpers';
import { EmptyState, ListNotices, StatusChip, TabIntro, TimeText } from './parts';
import type { Queue } from './useQueue';

export interface HistoryFilter {
  queue: HistoryQueue | '';
  status: HistoryStatus | '';
  offset: number;
}

interface Props {
  queue: Queue<HistoryList>;
  filter: HistoryFilter;
  onFilter: (next: HistoryFilter) => void;
  vaultName: (vaultId: string | null | undefined) => string;
}

const QUEUES: HistoryQueue[] = ['work-orders', 'closures', 'quarantine'];
const STATUSES: HistoryStatus[] = ['APPLIED', 'CANCELLED'];

/** "History": everything done or cancelled, newest first, filterable. The vault filter is the page's. */
export const HistoryTab = ({ queue, filter, onFilter, vaultName }: Props) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.history.${key}`, options);
  const items = queue.data?.items ?? [];
  const total = queue.data?.total ?? 0;

  /** "Payout confirmed", "Close-out settled", "Refund approved" — the outcome in words, never the engine's code. */
  const what = (row: HistoryRow) =>
    row.status === 'CANCELLED'
      ? t(`cancelledWhat.${row.queue}`)
      : t(`what.${row.queue}.${row.kind}`);
  const who = (row: HistoryRow) =>
    row.status === 'CANCELLED'
      ? t('cancelledBy', { name: adminName(row.cancelledByAdmin, row.cancelledBy) })
      : t('approvedBy', { name: adminName(row.approvedByAdmin, row.approvedBy) });

  return (
    <Stack spacing={2}>
      <TabIntro tab="history" />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label={t('queue')}
          value={filter.queue}
          onChange={(e) =>
            onFilter({ ...filter, queue: e.target.value as HistoryQueue | '', offset: 0 })
          }
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">{t('allQueues')}</MenuItem>
          {QUEUES.map((q) => (
            <MenuItem key={q} value={q}>
              {t(`queues.${q}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('status')}
          value={filter.status}
          onChange={(e) =>
            onFilter({ ...filter, status: e.target.value as HistoryStatus | '', offset: 0 })
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('allStatuses')}</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {translate(`${NS}.status.${s === 'APPLIED' ? 'DONE' : 'CANCELLED'}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          shown={items.length}
          onRefresh={queue.refetch}
        />
      )}
      {(queue.data?.capped?.length ?? 0) > 0 && (
        <Alert severity="info" data-testid="history-capped">
          {t('capped', {
            queues: (queue.data?.capped ?? []).map((q) => t(`queues.${q}`)).join(', '),
          })}
        </Alert>
      )}
      {queue.data && items.length === 0 && (
        <EmptyState tab="history" checkedAt={queue.data.checkedAt} />
      )}
      {items.length > 0 && (
        <TableContainer>
          <Table size="small" data-testid="history-table">
            <TableHead>
              <TableRow>
                {['doneAt', 'what', 'vault', 'amount', 'status', 'who', 'note'].map((col) => (
                  <TableCell key={col}>{t(`columns.${col}`, { zone: utcZoneLabel() })}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={`${row.queue}/${row.id}`} hover>
                  <TableCell>
                    <TimeText iso={row.doneAt} />
                  </TableCell>
                  <TableCell>{what(row)}</TableCell>
                  <TableCell>{row.vaultId ? vaultName(row.vaultId) : '—'}</TableCell>
                  <TableCell align="right">{money2(row.amount)}</TableCell>
                  <TableCell>
                    <StatusChip status={row.uxStatus} />
                  </TableCell>
                  <TableCell>
                    {doneBySystem(row) ? (
                      <Typography variant="body2">{t('doneBySystem')}</Typography>
                    ) : (
                      <>
                        <Typography variant="body2">
                          {t('proposedBy', {
                            name: adminName(row.proposedByAdmin, row.proposedBy),
                          })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {who(row)}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, overflowWrap: 'anywhere' }}>{row.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {total > HISTORY_PAGE_SIZE && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            sx={OUTCOME_BUTTON_SX}
            disabled={filter.offset === 0}
            onClick={() =>
              onFilter({ ...filter, offset: Math.max(0, filter.offset - HISTORY_PAGE_SIZE) })
            }
          >
            {t('previous')}
          </Button>
          <Typography variant="body2">
            {t('range', {
              from: filter.offset + 1,
              to: Math.min(total, filter.offset + HISTORY_PAGE_SIZE),
              total,
            })}
          </Typography>
          <Button
            sx={OUTCOME_BUTTON_SX}
            disabled={filter.offset + HISTORY_PAGE_SIZE >= total}
            onClick={() => onFilter({ ...filter, offset: filter.offset + HISTORY_PAGE_SIZE })}
          >
            {t('next')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
